import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { get, partial, sortBy } from 'lodash';
import {
  IShopCouponDiscount,
  ShopCouponTypesEnum,
  ShopCouponValueTypeEnum
} from '../../modules/types';
import { getTranslationKey } from '../../modules/utils';
import { InputItem, SelectItem } from '../Forms';
import { useSelector } from 'react-redux';
import {
  getLaboratoriesSelector,
  getProductGroupsList,
  getProductList,
  getUser
} from '../../modules/selectors';
import media from '../../assets/css/media';
import {
  fieldsMargin,
  productGroupsOption,
  productsOption,
  showValue,
  typeOptions,
  validateMaxQ,
  validatePercent
} from './helpers';

const Wrapper = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  height: 85px;
  margin-bottom: 10px;
  padding: 0 10px;
  @media (max-width: ${media.desktop}) {
    flex-direction: column;
    height: auto;
    & > div {
      width: 100%;
    }
  }
`;

interface IProp {
  index: number;
  updateDiscountCallback: (discount: IShopCouponDiscount, index: number) => void;
  discount: IShopCouponDiscount;
}

// tslint:disable-next-line:cyclomatic-complexity max-func-body-length
export const DiscountItem = ({ discount, index, updateDiscountCallback }: IProp) => {
  const productGroups = useSelector(getProductGroupsList);
  const user = useSelector(getUser);
  const products = useSelector(getProductList);
  const laboratories = useSelector(getLaboratoriesSelector);
  const { type, valueType, quantityMax, valueFixed, valuePercent } = discount;
  const showFixedValue = showValue(valueFixed);
  const [fixedValue, setFixedValue] = useState(showFixedValue);

  const changeDiscountHandler = (field: string, value: unknown) => {
    const isFixedValue = field === 'valueFixed';

    if (isFixedValue) {
      setFixedValue(value as string);
    }

    updateDiscountCallback(
      {
        ...discount,
        [field]: isFixedValue ? parseFloat(String(((value as number) * 100).toFixed())) : value
      },
      index
    );
  };

  const isValueTypeDisabled = ['total', 'shipment'].includes(type);
  const productLabel =
    isValueTypeDisabled || type === ShopCouponTypesEnum.Product
      ? getTranslationKey('shop.coupons.productLabel')
      : getTranslationKey('shop.coupons.productGroupLabel');
  const productSelectField = type === ShopCouponTypesEnum.Product ? 'productId' : 'productGroupId';
  const productSelectValue = get(discount, productSelectField, '');
  const valueTypeValue = valueType === ShopCouponValueTypeEnum.Fixed ? fixedValue : valuePercent;
  const valueTypeFiled =
    valueType === ShopCouponValueTypeEnum.Fixed ? 'valueFixed' : 'valuePercent';
  const valueTypeButtonValue =
    valueType === ShopCouponValueTypeEnum.Fixed
      ? getTranslationKey(`currencies.${user.currency}`)
      : '%';
  const valueTypeOptions =
    valueType === ShopCouponValueTypeEnum.Fixed
      ? [
          {
            value: ShopCouponValueTypeEnum.Percent,
            text: '%'
          }
        ]
      : [
          {
            value: ShopCouponValueTypeEnum.Fixed,
            text: getTranslationKey(`currencies.${user.currency}`)
          }
        ];
  const step = valueType === ShopCouponValueTypeEnum.Fixed ? '0.1' : '1';
  const productGroupOptions = productGroupsOption(productGroups);
  const printProducts = products.filter((product) =>
    laboratories.some((lab) => lab.id === product.laboratory)
  );
  const productsOptions = productsOption(sortBy(printProducts, 'laboratory'), laboratories);

  useEffect(() => {
    const validated = validateMaxQ(quantityMax);
    if (!isValueTypeDisabled && validated !== quantityMax) {
      changeDiscountHandler('quantityMax', validated);
    }
  }, [quantityMax, isValueTypeDisabled]); // eslint-disable-line

  useEffect(() => {
    if ([ShopCouponTypesEnum.ProductGroup, ShopCouponTypesEnum.Product].indexOf(type) === -1) {
      updateDiscountCallback(
        { ...discount, productId: products[0].id, productGroupId: null, quantityMax: null },
        index
      );
    }
  }, [type]); // eslint-disable-line

  useEffect(() => {
    const validated = validatePercent(valuePercent);
    if (valueType === ShopCouponValueTypeEnum.Percent && validated !== valuePercent) {
      changeDiscountHandler('valuePercent', validated);
    }
  }, [valueType, valuePercent]); // eslint-disable-line

  return (
    <Wrapper>
      <SelectItem
        label={getTranslationKey('shop.coupons.typeLabel')}
        options={typeOptions()}
        value={type}
        changeCallback={partial(changeDiscountHandler, 'type')}
        margin={fieldsMargin}
      />
      <SelectItem
        defaultOptionsText={type === ShopCouponTypesEnum.Product ? null : '-'}
        label={productLabel}
        options={type === ShopCouponTypesEnum.Product ? productsOptions : productGroupOptions}
        value={String(productSelectValue)}
        disabled={isValueTypeDisabled}
        changeCallback={partial(changeDiscountHandler, productSelectField)}
        margin={fieldsMargin}
        required={type === ShopCouponTypesEnum.ProductGroup}
      />
      <InputItem
        label={getTranslationKey('shop.coupons.maxLabel')}
        type="number"
        changeCallback={partial(changeDiscountHandler, 'quantityMax')}
        value={String(quantityMax)}
        placeholder="-"
        disabled={isValueTypeDisabled}
        min={1}
        margin={fieldsMargin}
        withTooltip
        tooltipText={getTranslationKey('shop.coupons.maxQuantityTooltip')}
        id="quantityMax"
      />
      <InputItem
        label={getTranslationKey('shop.coupons.valueLabel')}
        type="number"
        changeCallback={partial(changeDiscountHandler, valueTypeFiled)}
        value={String(valueTypeValue)}
        placeholder="-"
        min={1}
        step={step}
        margin={fieldsMargin}
        dropdownConfig={{
          buttonLabel: valueTypeButtonValue,
          options: valueTypeOptions,
          callback: partial(changeDiscountHandler, 'valueType')
        }}
        required
      />
    </Wrapper>
  );
};
